import { EnhancedFormikForm } from '@/components/EnhancedFormikForm';
import { Box, Button } from '@palmetto/palmetto-components';
import { Formik, FormikValues } from 'formik';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import { ConstructionAdderType, EquipmentType, ProgramType } from 'types';

import { ConstructionAddersForm } from './ConstructionAddersForm';
import { EnergyBackupOptions } from './EnergyBackupOptions';
import { ModifiersForm } from './ModifiersForm';
import { Pricing } from './PricingForm';
import { TargetAccounts } from './TargetAccountsForm';

const initialSchemaByProgramType: any = {
  [ProgramType.solar]: {
    organizationIds: [],
    priceSheet: '',
    escMin: '',
    escMax: '',
    pvOnlyMin: '',
    pvOnlyMax: '',
    absoluteCeiling: '',
    electricalUpgrade: '',
    arbitrageBattery: '',
    backupBattery: '',
    backupBatteryKwh: '',
    batteryMinCapacity: '',
    holdback: '',
    zeroEscalatorCeiling: '',
    energyCommunity: '',
    domesticContent: '',
    state: '',
    utilities: [],
  },
  [ProgramType.doePr]: {
    organizationIds: [],
    priceSheet: '',
    escMin: '',
    escMax: '',
    utilities: [],
    minSystemSize: '',
    maxSystemSize: '',
    minBatteryCapacity: '',
    maxBatteryCapacity: '',
    state: { label: 'PR', value: 'PR' },
    solarReadiness: '',
    backupBattery: '',
  },
  [ProgramType.hvac]: {
    organizationIds: [],
    states: [],
    priceSheet: '',
    escMin: '',
    escMax: '',
    minTerm: '',
    maxTerm: '',
    minSystemCost: '',
    maxSystemCost: '',
    isWarrantyCostIncluded: true,
    estimatedWarrantyCost: 0,
  },
  [ProgramType.waterHeater]: {
    organizationIds: [],
    states: [],
    priceSheet: '',
    escMin: '',
    escMax: '',
    minTerm: '',
    maxTerm: '',
    minSystemCost: '',
    maxSystemCost: '',
    isWarrantyCostIncluded: true,
    estimatedWarrantyCost: 0,
  },
  [ProgramType.generator]: {
    organizationIds: [],
    states: [],
    priceSheet: '',
    escMin: '',
    escMax: '',
    minTerm: '',
    maxTerm: '',
    minSystemCost: '',
    maxSystemCost: '',
  },
  [ProgramType.newHomes]: {
    organizationIds: [],
    priceSheet: '',
    escMin: '',
    escMax: '',
    state: '',
    utilities: [],
    pvOnlyMin: '',
    pvOnlyMax: '',
    absoluteCeiling: '',
    electricalUpgrade: '',
    arbitrageBattery: '',
    backupBattery: '',
    backupBatteryKwh: '',
    holdback: '',
    zeroEscalatorCeiling: '',
    energyCommunity: '',
    domesticContent: '',
  },
  [ProgramType.geothermal]: {
    organizationIds: [],
    states: [],
    priceSheet: '',
    escMin: '',
    escMax: '',
    minTerm: '',
    maxTerm: '',
    minSystemCost: '',
    maxSystemCost: '',
  },
  [ProgramType.energyBackup]: {
    organizationIds: [],
    state: '',
    utilities: [],
    energyBackupOptions: {
      backupBattery: true,
      arbitrageBattery: true,
    },
    priceSheet: '',
    escMin: '',
    escMax: '',
    minTerm: '',
    maxTerm: '',
    minSystemCost: '',
    maxSystemCost: '',
  },
  [ProgramType.consumerSolar]: {
    organizationIds: [],
    priceSheet: '',
    escMin: '',
    escMax: '',
    utilities: [],
    minSystemSize: '',
    maxSystemSize: '',
    minProductionFactor: '',
    maxProductionFactor: '',
    state: '',
    arbitrageBattery: '',
  },
};

const validationSchemaByProgramType: any = {
  [ProgramType.solar]: yup.object({
    organizationIds: yup.array().min(1),
    priceSheet: yup.object().required('Price Sheet is required'),
    escMin: yup.object().required('Escalator Min is required'),
    escMax: yup.object().required('Escalator Max is required'),
    state: yup.object().required('State is required'),
    utilities: yup.array().min(1),
    pvOnlyMin: yup.object().required('PV-Only Min is required'),
    pvOnlyMax: yup.object().required('PV-Only Max is required'),
    absoluteCeiling: yup.object().required('Absolute Ceiling is required'),
    electricalUpgrade: yup.object().nullable(),
    arbitrageBattery: yup.object().nullable(),
    zeroEscalatorCeiling: yup.object().nullable(),
  }),
  [ProgramType.newHomes]: yup.object({
    organizationIds: yup.array().min(1),
    priceSheet: yup.object().required('Price Sheet is required'),
    escMin: yup.object().required('Escalator Min is required'),
    escMax: yup.object().required('Escalator Max is required'),
    state: yup.object().required('State is required'),
    utilities: yup.array().min(1),
    pvOnlyMin: yup.object().required('PV-Only Min is required'),
    pvOnlyMax: yup.object().required('PV-Only Max is required'),
    absoluteCeiling: yup.object().required('Absolute Ceiling is required'),
    electricalUpgrade: yup.object().nullable(),
    arbitrageBattery: yup.object().nullable(),
    zeroEscalatorCeiling: yup.object().nullable(),
  }),
  [ProgramType.doePr]: yup.object({
    organizationIds: yup.array().min(1),
    priceSheet: yup.object().required('Price Sheet is required'),
    escMin: yup.object().required('Escalator Min is required'),
    escMax: yup.object().required('Escalator Max is required'),
    state: yup.object().required('State is required'),
    utilities: yup.array().min(1),
    minSystemSize: yup.object().required('Min System Size is required'),
    maxSystemSize: yup.object().required('Max System Size is required'),
    minBatteryCapacity: yup.object().required('Min Battery Capacity is required'),
    maxBatteryCapacity: yup.object().required('Max Battery Capacity is required'),
    backupBattery: yup.object().required('Backup Battery is required'),
  }),
  [ProgramType.hvac]: yup.object({
    organizationIds: yup.array().min(1),
    priceSheet: yup.object().required('Price Sheet is required'),
    escMin: yup.object().required('Escalator Min is required'),
    escMax: yup.object().required('Escalator Max is required'),
    states: yup.array().min(1),
    minTerm: yup.object().required('Min Term is required'),
    maxTerm: yup.object().required('Max Term is required'),
    minSystemCost: yup.object().required('Min System Cost is required'),
    maxSystemCost: yup.object().required('Max System Cost is required'),
    isWarrantyCostIncluded: yup.boolean().required('Warranty Cost Inclusion is required'),
    estimatedWarrantyCost: yup
      .number()
      .required('Estimated Warranty Cost is required')
      .min(0, 'Estimated Warranty Cost must be greater than or equal to 0'),
  }),
  [ProgramType.waterHeater]: yup.object({
    organizationIds: yup.array().min(1),
    priceSheet: yup.object().required('Price Sheet is required'),
    escMin: yup.object().required('Escalator Min is required'),
    escMax: yup.object().required('Escalator Max is required'),
    states: yup.array().min(1),
    minTerm: yup.object().required('Min Term is required'),
    maxTerm: yup.object().required('Max Term is required'),
    minSystemCost: yup.object().required('Min System Cost is required'),
    maxSystemCost: yup.object().required('Max System Cost is required'),
    isWarrantyCostIncluded: yup.boolean().required('Warranty Cost Inclusion is required'),
    estimatedWarrantyCost: yup
      .number()
      .required('Estimated Warranty Cost is required')
      .min(0, 'Estimated Warranty Cost must be greater than or equal to 0'),
  }),
  [ProgramType.generator]: yup.object({
    organizationIds: yup.array().min(1),
    priceSheet: yup.object().required('Price Sheet is required'),
    escMin: yup.object().required('Escalator Min is required'),
    escMax: yup.object().required('Escalator Max is required'),
    states: yup.array().min(1),
    minTerm: yup.object().required('Min Term is required'),
    maxTerm: yup.object().required('Max Term is required'),
    minSystemCost: yup.object().required('Min System Cost is required'),
    maxSystemCost: yup.object().required('Max System Cost is required'),
  }),
  [ProgramType.geothermal]: yup.object({
    organizationIds: yup.array().min(1),
    priceSheet: yup.object().required('Price Sheet is required'),
    escMin: yup.object().required('Escalator Min is required'),
    escMax: yup.object().required('Escalator Max is required'),
    states: yup.array().min(1),
    minTerm: yup.object().required('Min Term is required'),
    maxTerm: yup.object().required('Max Term is required'),
    minSystemCost: yup.object().required('Min System Cost is required'),
    maxSystemCost: yup.object().required('Max System Cost is required'),
  }),
  [ProgramType.energyBackup]: yup.object({
    organizationIds: yup.array().min(1, 'At least one organization is required'),
    state: yup.object().required('State is required'),
    utilities: yup.array().min(1, 'At least one utility is required'),
    priceSheet: yup.object().required('Price Sheet is required'),
    escMin: yup.object().required('Escalator Min is required'),
    escMax: yup.object().required('Escalator Max is required'),
    states: yup.array().min(1),
    minTerm: yup.object().required('Min Term is required'),
    maxTerm: yup.object().required('Max Term is required'),
    minSystemCost: yup.object().required('Min System Cost is required'),
    maxSystemCost: yup.object().required('Max System Cost is required'),
  }),
  [ProgramType.consumerSolar]: yup.object({
    organizationIds: yup.array().min(1),
    priceSheet: yup.object().required('Price Sheet is required'),
    escMin: yup.object().required('Escalator Min is required'),
    escMax: yup.object().required('Escalator Max is required'),
    state: yup.object().required('State is required'),
    utilities: yup.array().min(1),
    minSystemSize: yup.object().required('Min System Size is required'),
    maxSystemSize: yup.object().required('Max System Size is required'),
    minProductionFactor: yup.object().required('Min Production is required'),
    maxProductionFactor: yup.object().required('Max Production is required'),
  }),
};

export const isFieldSelected = (field: any) => {
  return field && field !== '' && field.value;
};

interface MappingFactoryProps {
  handleSubmit: (data: any) => void;
  programType: ProgramType;
  data?: FormikValues;
}

export const MappingFormFactory = ({ handleSubmit, programType, data = {} }: MappingFactoryProps) => {
  const initialValues = {
    ...initialSchemaByProgramType[programType],
    ...data,
  };
  const validationSchema = validationSchemaByProgramType[programType];

  const navigate = useNavigate();

  const onSubmit = (values: any) => {
    let data;
    switch (programType) {
      case ProgramType.newHomes:
      case ProgramType.solar: {
        const constructionAdders: any[] = [];
        const selectedArbitrageBattery = values.selectedArbitrageBattery;
        const selectedArbitrageBatteryCost = values.selectedArbitrageBatteryCost;
        const selectedBackupBattery = values.selectedBackupBattery;
        const selectedBackupBatteryKwh = values.selectedBackupBatteryKwh;
        const selectedHoldback = values.selectedHoldback;
        const selectedEnergyCommunity = values.selectedEnergyCommunity;
        const selectedElectricalUpgrade = values.selectedElectricalUpgrade;
        const selectedBackupBatteryPriceCaps = values.selectedBackupBatteryPriceCaps;
        const selectedArbitrageBatteryPriceCaps = values.selectedArbitrageBatteryPriceCaps;
        const selectedPpwModifier = values.selectedPpwModifier;
        const selectedDomesticContentModifier = values.selectedDomesticContentModifier;
        const selectedBatteryMinCapacity = values.selectedBatteryMinCapacity;

        const arbitrageBattery = selectedArbitrageBattery && {
          id: values.arbitrageBattery.value,
          name: values.arbitrageBattery.label,
          type: ConstructionAdderType.arbitrageBattery,
          kwhAdderBand: selectedArbitrageBattery.kwhAdderBand,
        };
        const arbitrageBatteryCost = selectedArbitrageBatteryCost && {
          id: values.arbitrageBatteryCost.value,
          name: values.arbitrageBatteryCost.label,
          type: ConstructionAdderType.arbitrageBatteryCost,
          paymentFactors: selectedArbitrageBatteryCost.paymentFactors,
        };
        const backupBattery = selectedBackupBattery && {
          id: values.backupBattery.value,
          name: values.backupBattery.label,
          type: ConstructionAdderType.backupBattery,
          paymentFactors: selectedBackupBattery.paymentFactors,
        };
        const backupBatteryKwh = selectedBackupBatteryKwh && {
          id: values.backupBatteryKwh.value,
          name: values.backupBatteryKwh.label,
          type: ConstructionAdderType.backupBatteryKwh,
          kwhAdderBand: selectedBackupBatteryKwh.kwhAdderBand,
        };
        const electricalUpgrade = selectedElectricalUpgrade && {
          id: values.electricalUpgrade.value,
          name: values.electricalUpgrade.label,
          type: ConstructionAdderType.electricalUpgrade,
          kwhAdderBand: selectedElectricalUpgrade.kwhAdderBand,
        };
        const backupBatteryPriceCaps = selectedBackupBatteryPriceCaps && {
          id: values.backupBatteryPriceCaps.value,
          name: values.backupBatteryPriceCaps.label,
          type: ConstructionAdderType.backupBatteryPriceCap,
          backupBatteryPriceCaps: selectedBackupBatteryPriceCaps.backupBatteryPriceCaps,
        };
        const arbitrageBatteryPriceCaps = selectedArbitrageBatteryPriceCaps && {
          id: values.arbitrageBatteryPriceCaps.value,
          name: values.arbitrageBatteryPriceCaps.label,
          type: ConstructionAdderType.arbitrageBatteryPriceCap,
          arbitrageBatteryPriceCaps: selectedArbitrageBatteryPriceCaps.arbitrageBatteryPriceCaps,
        };
        const batteryMinimumCapacity = selectedBatteryMinCapacity && {
          id: values.batteryMinCapacity.value,
          name: values.batteryMinCapacity.label,
          type: ConstructionAdderType.batteryMinimumCapacity,
          minCapacity: selectedBatteryMinCapacity.minCapacity,
        };
        constructionAdders.push(arbitrageBattery);
        constructionAdders.push(arbitrageBatteryCost);
        constructionAdders.push(electricalUpgrade);
        constructionAdders.push(backupBattery);
        constructionAdders.push(backupBatteryKwh);
        constructionAdders.push(backupBatteryPriceCaps);
        constructionAdders.push(arbitrageBatteryPriceCaps);
        constructionAdders.push(batteryMinimumCapacity);

        const energyCommunity = selectedEnergyCommunity && {
          id: selectedEnergyCommunity.id,
          name: selectedEnergyCommunity.name,
          values: selectedEnergyCommunity.data.flatMap((item: any) => {
            const systemType = item.systemType;
            const values = Object.keys(item).reduce(
              (acc, key) => {
                if (typeof item[key] === 'string') {
                  return acc;
                }
                const obj: Record<string, any> = {
                  type: key,
                  format: item[key].format,
                  value: item[key].value,
                  systemType,
                };
                acc.push(obj);
                return acc;
              },
              [] as Record<string, any>[],
            );
            return values;
          }),
        };

        const ppwModifier = selectedPpwModifier && {
          id: values.ppwModifier.value,
          name: values.ppwModifier.label,
          amount: selectedPpwModifier.data.amount,
        };

        const domesticContentModifier = selectedDomesticContentModifier && {
          id: selectedDomesticContentModifier.id,
          name: selectedDomesticContentModifier.name,
          values: selectedDomesticContentModifier.data,
        };

        data = {
          organizationIds: values.organizationIds.map((v: any) => v.value),
          state: values.state.value,
          utilities: values.utilities.map((v: any) => v.value),
          priceSheetId: values.priceSheet.value,
          priceSheetName: values.priceSheet.label,
          escalationRateMin: values.escMin.value,
          escalationRateMax: values.escMax.value,
          zeroEscalatorCeiling: values.zeroEscalatorCeiling.value,
          pvOnlyMin: values.pvOnlyMin.value,
          pvOnlyCeiling: values.pvOnlyMax.value,
          absoluteCeiling: values.absoluteCeiling.value,
          constructionAdders: constructionAdders.filter((adder) => !!adder),
          holdback: selectedHoldback,
          energyCommunity,
          ppwModifier,
          domesticContentModifier,
          programType,
        };
        break;
      }
      case ProgramType.doePr: {
        const constructionAdders: any[] = [];
        const selectedBackupBattery = values.selectedBackupBattery;
        const selectedSolarReadiness = values.selectedSolarReadiness;
        const solarReadiness = selectedSolarReadiness && {
          id: values.solarReadiness.value,
          name: values.solarReadiness.label,
          type: ConstructionAdderType.solarReadiness,
          maxAmount: selectedSolarReadiness.maxAmount,
        };
        const backupBattery = selectedBackupBattery && {
          id: values.backupBattery.value,
          name: values.backupBattery.label,
          type: ConstructionAdderType.backupBattery,
          paymentFactors: selectedBackupBattery.paymentFactors,
        };
        constructionAdders.push(solarReadiness);
        constructionAdders.push(backupBattery);
        data = {
          organizationIds: values.organizationIds.map((v: any) => v.value),
          state: values.state.value,
          utilities: values.utilities.map((v: any) => v.value),
          priceSheetId: values.priceSheet.value,
          priceSheetName: values.priceSheet.label,
          escalationRateMin: values.escMin.value,
          escalationRateMax: values.escMax.value,
          minSystemSize: values.minSystemSize.value,
          maxSystemSize: values.maxSystemSize.value,
          minBatteryCapacity: values.minBatteryCapacity.value,
          maxBatteryCapacity: values.maxBatteryCapacity.value,
          constructionAdders: constructionAdders.filter((adder) => !!adder),
          programType,
        };
        break;
      }
      case ProgramType.consumerSolar: {
        const selectedArbitrageBattery = values.selectedArbitrageBattery;
        const arbitrageBattery = selectedArbitrageBattery && {
          id: values.arbitrageBattery.value,
          name: values.arbitrageBattery.label,
          type: ConstructionAdderType.arbitrageBattery,
          kwhAdderBand: selectedArbitrageBattery.kwhAdderBand,
        };
        data = {
          organizationIds: values.organizationIds.map((v: any) => v.value),
          state: values.state.value,
          utilities: values.utilities.map((v: any) => v.value),
          priceSheetId: values.priceSheet.value,
          priceSheetName: values.priceSheet.label,
          escalationRateMin: values.escMin.value,
          escalationRateMax: values.escMax.value,
          minSystemSize: values.minSystemSize.value,
          maxSystemSize: values.maxSystemSize.value,
          minProductionFactor: values.minProductionFactor.value,
          maxProductionFactor: values.maxProductionFactor.value,
          constructionAdders: [arbitrageBattery].filter((adder) => !!adder),
          programType,
        };
        break;
      }
      case ProgramType.hvac:
      case ProgramType.waterHeater: {
        data = {
          organizationIds: values.organizationIds.map((v: any) => v.value),
          states: Array.isArray(values.states) ? values.states.map((s: any) => s.value) : [],
          priceSheetId: values.priceSheet.value,
          priceSheetName: values.priceSheet.label,
          escalationRateMin: values.escMin.value,
          escalationRateMax: values.escMax.value,
          minTerm: values.minTerm.value,
          maxTerm: values.maxTerm.value,
          minSystemCost: values.minSystemCost.value,
          maxSystemCost: values.maxSystemCost.value,
          programType,
          isWarrantyCostIncluded: values.isWarrantyCostIncluded,
          estimatedWarrantyCost: values.estimatedWarrantyCost,
        };
        break;
      }
      case ProgramType.generator: {
        data = {
          organizationIds: values.organizationIds.map((v: any) => v.value),
          states: Array.isArray(values.states) ? values.states.map((s: any) => s.value) : [],
          priceSheetId: values.priceSheet.value,
          priceSheetName: values.priceSheet.label,
          escalationRateMin: values.escMin.value,
          escalationRateMax: values.escMax.value,
          minTerm: values.minTerm.value,
          maxTerm: values.maxTerm.value,
          minSystemCost: values.minSystemCost.value,
          maxSystemCost: values.maxSystemCost.value,
          programType,
        };
        break;
      }
      case ProgramType.geothermal: {
        data = {
          organizationIds: values.organizationIds.map((v: any) => v.value),
          states: Array.isArray(values.states) ? values.states.map((s: any) => s.value) : [],
          priceSheetId: values.priceSheet.value,
          priceSheetName: values.priceSheet.label,
          escalationRateMin: values.escMin.value,
          escalationRateMax: values.escMax.value,
          minTerm: values.minTerm.value,
          maxTerm: values.maxTerm.value,
          minSystemCost: values.minSystemCost.value,
          maxSystemCost: values.maxSystemCost.value,
          programType,
        };
        break;
      }
      case ProgramType.energyBackup: {
        const selectedBackupBatteryCap = values.selectedBackupBatteryCap;
        const selectedArbBatteryCap = values.selectedArbitrageCap;
        const backupBatteryCap = selectedBackupBatteryCap && {
          id: values.backupBatteryCap.value,
          name: values.backupBatteryCap.label,
          type: ConstructionAdderType.backupBatteryPriceCap,
          energyBackupCaps: selectedBackupBatteryCap.energyBackupCaps,
          energyBackupCapSchedules: selectedBackupBatteryCap.energyBackupCapSchedules,
        };
        const arbBatteryCap = selectedArbBatteryCap && {
          id: values.arbitrageBatteryCap.value,
          name: values.arbitrageBatteryCap.label,
          type: ConstructionAdderType.arbitrageBatteryPriceCap,
          energyBackupCaps: selectedArbBatteryCap.energyBackupCaps,
          energyBackupCapSchedules: selectedArbBatteryCap.energyBackupCapSchedules,
        };
        data = {
          organizationIds: values.organizationIds.map((v: any) => v.value),
          state: values.state.value,
          utilities: values.utilities.map((v: any) => v.value),
          priceSheetId: values.priceSheet.value,
          priceSheetName: values.priceSheet.label,
          escalationRateMin: values.escMin.value,
          escalationRateMax: values.escMax.value,
          minTerm: values.minTerm.value,
          maxTerm: values.maxTerm.value,
          minSystemCost: values.minSystemCost.value,
          maxSystemCost: values.maxSystemCost.value,
          constructionAdders: [backupBatteryCap, arbBatteryCap].filter((adder) => !!adder),
          equipmentTypes: [values?.energyBackupOptions?.backupBattery ? EquipmentType.backupBattery : null].filter(
            Boolean,
          ),
          programType,
        };
        break;
      }
      default:
        break;
    }
    handleSubmit(data);
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      enableReinitialize={true}
    >
      {({ handleSubmit, isSubmitting, resetForm }) => (
        <EnhancedFormikForm onSubmit={handleSubmit} noValidate>
          <Box childGap="xl">
            <Box>
              <TargetAccounts programType={programType} />
            </Box>
            <Box>
              <EnergyBackupOptions programType={programType} />
            </Box>
            <Box>
              <Pricing programType={programType} />
            </Box>
            <Box>
              <ConstructionAddersForm programType={programType} initialValues={initialValues} />
            </Box>
            <Box>
              <ModifiersForm programType={programType} initialValues={initialValues} />
            </Box>
            <Box direction="row" justifyContent="flex-end" childGap="sm">
              <Box>
                <Button
                  as="button"
                  onClick={() => {
                    resetForm();
                    navigate(`/admin/pricing/${programType}`);
                  }}
                  variant="secondary"
                  tone="neutral"
                  size="md"
                  isDisabled={isSubmitting}
                >
                  Cancel
                </Button>
              </Box>
              <Box>
                <Button size="md" variant="primary" type="submit" isLoading={isSubmitting}>
                  Save Mapping
                </Button>
              </Box>
            </Box>
          </Box>
        </EnhancedFormikForm>
      )}
    </Formik>
  );
};
